import {
  createContext, useState, useEffect, useMemo, useContext, useCallback,
} from 'react';
import PropTypes from 'prop-types';

const SettingsContext = createContext();

// Helper to load setting from localStorage with default value
const loadSetting = (key, defaultValue) => {
  const stored = localStorage.getItem(key);
  return stored !== null ? JSON.parse(stored) : defaultValue;
};

export function SettingsProvider({ children }) {
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showTimer, setShowTimer] = useState(true);
  const [showMoveCount, setShowMoveCount] = useState(true);
  const [showPreview, setShowPreview] = useState(true);
  const [confettiEnabled, setConfettiEnabled] = useState(true);
  const [theme, setTheme] = useState('light');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load all settings from localStorage on mount
    setSoundEnabled(loadSetting('soundEnabled', true));
    setShowTimer(loadSetting('showTimer', true));
    setShowMoveCount(loadSetting('showMoveCount', true));
    setShowPreview(loadSetting('showPreview', true));
    setConfettiEnabled(loadSetting('confettiEnabled', true));
    setTheme(loadSetting('theme', 'light'));
    setLoading(false);
  }, []);

  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const newValue = !prev;
      localStorage.setItem('soundEnabled', JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  const toggleShowTimer = useCallback(() => {
    setShowTimer((prev) => {
      const newValue = !prev;
      localStorage.setItem('showTimer', JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  const toggleShowMoveCount = useCallback(() => {
    setShowMoveCount((prev) => {
      const newValue = !prev;
      localStorage.setItem('showMoveCount', JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  const toggleShowPreview = useCallback(() => {
    setShowPreview((prev) => {
      const newValue = !prev;
      localStorage.setItem('showPreview', JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  const toggleConfetti = useCallback(() => {
    setConfettiEnabled((prev) => {
      const newValue = !prev;
      localStorage.setItem('confettiEnabled', JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const newValue = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', JSON.stringify(newValue));
      return newValue;
    });
  }, []);

  const value = useMemo(() => ({
    soundEnabled,
    toggleSound,
    showTimer,
    toggleShowTimer,
    showMoveCount,
    toggleShowMoveCount,
    showPreview,
    toggleShowPreview,
    confettiEnabled,
    toggleConfetti,
    theme,
    toggleTheme,
    loading,
  }), [
    soundEnabled, toggleSound,
    showTimer, toggleShowTimer,
    showMoveCount, toggleShowMoveCount,
    showPreview, toggleShowPreview,
    confettiEnabled, toggleConfetti,
    theme, toggleTheme,
    loading,
  ]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

SettingsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

export default SettingsContext;
